import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { Checkbox } from '@sas/nova-commons/components/Checkbox';
import { Divider } from '@sas/nova-commons/components/Divider';
import { Form } from '@sas/nova-commons/components/Layouts/Form';
import { Icon } from '@sas/nova-commons/components/Icon';
import { NumericStepper } from '@sas/nova-commons/components/NumericStepper';
import { DragAndDropProvider } from '@sas/nova-commons/dnd/DragAndDropProvider';
import { Input, useInputValidationAdapter, } from '@sas/nova-commons/components/Input';
import { Label } from '@sas/nova-commons/components/Label';
import { Flow } from '@sas/nova-commons/components/Layouts/Flow';
import { List } from '@sas/nova-commons/components/List';
import { Toolbar } from '@sas/nova-commons/components/Toolbar';
import { useListState } from '@sas/nova-commons/components/List/useListState';
import { withLabels } from '@sas/nova-commons/components/withLabels';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { useGraphTokens } from '@sas/nova-commons/theme/theme';
import { Add, BpmnEndEvent, ConditionalHighlightingBullet, Delete, GeoUIRoutePinLocation, GrabberReorderList, GrabberVertical, LinkBroken, ReverseRoute, } from '@sas/react-svg';
import React, { useState, useRef, useEffect, useMemo, Suspense } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../../../locale/I18nContext';
import { ObjectSearchState, isValidRoute, } from '../../../../store/objectSearch/ObjectSearchState';
import { selectObjectSearchState } from '../../../../store/selectors';
import { useLatestRef, useOnUnmount } from '../../../hooks';
import { useMinPanelHeight } from '../../hooks/useMinPanelHeight';
import { PanelHeader } from '../components/PanelHeader';
import { DriveTimeTrafficType, MapUnits, RoutingMode } from '@sas-dvr/ltjs-commons';
import { PanelSearchField } from '../components/PanelSearchField';
import { TravelTypeField } from '../components/TravelTypeField';
import { createUseHtmlId } from '@sas/nova-commons/hooks/useHtmlId';
import { useRefArray } from '@sas/nova-commons/hooks/useRefArray';
import { getFeatureTokens, preventColorInversionStyles } from '../../utils/tokens';
import { FeatureColorPicker } from '../components/FeatureColorPicker';
import { useParentSize } from '@sas/nova-commons/hooks/useParentSize';
import cx from 'classnames';
import './RoutePanel.css';
import cn from './RoutePanel.css.json';
const useRoutePanelId = createUseHtmlId('RoutePanel');
const NumericStepperWithLabels = withLabels(NumericStepper);
export const RoutePanelBase = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // Hooks
    const localize = useI18n();
    const dt = useDesignTokens();
    const gt = useGraphTokens();
    const headingId = useRoutePanelId();
    const [setListParentRef, listParentDimensions] = useParentSize({
        // Initial dimensions of 0 prevent the table from flashing at the wrong size
        initialDimensions: { height: 0, width: 0 },
    });
    // Local states
    const previousRoute = useRef();
    const numberOfRows = props.route.routeLocations.length;
    const selectedRouteLocations = useMemo(() => {
        return props.route.routeLocations.reduce((set, next, i) => {
            if (typeof next !== 'number' && next.selected) {
                set.add(i.toString());
            }
            return set;
        }, new Set());
    }, [props.route.routeLocations]);
    const listState = useListState({
        selectedRows: selectedRouteLocations,
    });
    const { setSelectRow, setSelectAllRows, setSelectRows } = listState;
    const { selectedRows } = listState.preference;
    const updateRouteSelection = useRef(false);
    const validateRouteLocations = useRef(true);
    const [routeWidthEnabled, setRouteWidthEnabled] = useState(props.route.routeWidth !== undefined);
    const [locationCandidateIndex, setLocationCandidateIndex] = useState(-1);
    const headerRef = useRef(null);
    const measureContainerRef = useRef(null);
    const footerRef = useRef(null);
    useMinPanelHeight(headerRef, footerRef, (minPanelHeight) => {
        props.actions.update(props.id, { minPanelHeight });
    });
    /* basic validator for name input */
    const nameInputValidator = useInputValidationAdapter({
        isRequired: true,
        minLength: 1,
        initialValue: props.route.name,
    });
    {
        const refHandleChange = useLatestRef(nameInputValidator.handleChange);
        useEffect(() => {
            refHandleChange.current({ value: props.route.name });
        }, [refHandleChange, props.route.name]);
    }
    const routeLocationsValid = isValidRoute(props.route.routeLocations);
    let isFormValid;
    {
        // validate the form entries
        isFormValid = routeLocationsValid;
        // returns true if all route locations are valid
        if (nameInputValidator.errors.status) {
            isFormValid = false; // pin name invalid
        }
        if (props.route.isRendered) {
            isFormValid = false; // rendered route is up to date
        }
    }
    const isTimeTravel = props.route.mode === RoutingMode.DRIVING_TIME ||
        props.route.mode === RoutingMode.TRUCKING_TIME ||
        props.route.mode === RoutingMode.WALKING_TIME;
    const isRouteWidthTimeTravel = ((_a = props.route.routeWidth) === null || _a === void 0 ? void 0 : _a.mode) === RoutingMode.DRIVING_TIME ||
        ((_b = props.route.routeWidth) === null || _b === void 0 ? void 0 : _b.mode) === RoutingMode.TRUCKING_TIME ||
        ((_c = props.route.routeWidth) === null || _c === void 0 ? void 0 : _c.mode) === RoutingMode.WALKING_TIME;
    // re-order table rows
    const reorderTableRows = (_fromRows, toRow, rows) => {
        if (rows && toRow !== -1) {
            const result = [...props.route.routeLocations];
            const rowIndices = Array.from(rows)
                .map((rowKey) => parseInt(rowKey))
                .sort((x1, x2) => x2 - x1);
            const deleted = [];
            rowIndices === null || rowIndices === void 0 ? void 0 : rowIndices.forEach((index) => {
                const [removed] = result.splice(index, 1);
                deleted.push(removed);
            });
            result.splice(Math.min(toRow, result.length), 0, ...deleted);
            props.actions.notifyUpdateRoute(props.id, {
                index: props.featureIndex,
                update: {
                    routeLocations: result,
                },
            });
        }
    };
    // removes selected route locations from the list
    const deleteSelectedRouteLocations = () => {
        if (selectedRouteLocations) {
            Array.from(selectedRouteLocations) // convert to array
                .map((index) => parseInt(index)) // convert to numbers
                .sort((a, b) => b - a) // sort descending
                .forEach((index) => {
                // delete items from array
                props.route.routeLocations.splice(index, 1);
            });
            props.actions.notifyUpdateRoute(props.id, {
                index: props.featureIndex,
                update: { routeLocations: props.route.routeLocations },
            });
        }
    };
    // reverse the order of route locations
    const reverseRouteLocations = () => {
        props.actions.notifyUpdateRoute(props.id, {
            index: props.featureIndex,
            update: { routeLocations: props.route.routeLocations.reverse() },
        });
    };
    // append a new route location to the list
    const addRouteLocation = () => {
        props.actions.notifyUpdateRoute(props.id, {
            index: props.featureIndex,
            update: {
                routeLocations: props.route.routeLocations.concat([
                    {
                        name: `Pin ${props.route.routeLocations.length + 1}`,
                    },
                ]),
            },
        });
    };
    // reset list of route locations
    const clearRouteLocations = () => {
        props.actions.notifyClearRoute(props.id, { index: props.featureIndex });
    };
    // removes the link to an existing location pin
    const unlinkRouteLocations = () => {
        props.actions.notifyUpdateRoute(props.id, {
            index: props.featureIndex,
            update: {
                routeLocations: props.route.routeLocations.map((routeLocation, index) => {
                    var _a;
                    if (props.features &&
                        typeof routeLocation === 'number' &&
                        (selectedRouteLocations === null || selectedRouteLocations === void 0 ? void 0 : selectedRouteLocations.has(index.toString()))) {
                        const linkedPin = (_a = props.features) === null || _a === void 0 ? void 0 : _a.find((feature, index) => {
                            return feature.type === 'pin' && index === routeLocation;
                        });
                        return linkedPin ? linkedPin : routeLocation;
                    }
                    return routeLocation;
                }),
            },
        });
    };
    const firstEmptyFieldIndex = props.route.routeLocations.findIndex((location) => {
        return typeof location !== 'number' && !location.location;
    });
    // Move focus between route locations as they're filled in
    const routeLocationRefs = useRefArray();
    useEffect(() => {
        const emptyLocation = routeLocationRefs.refs[firstEmptyFieldIndex];
        emptyLocation === null || emptyLocation === void 0 ? void 0 : emptyLocation.focus();
    }, [routeLocationRefs, firstEmptyFieldIndex]);
    const geoRouteStyles = renderInlineCssVars({
        'spacing-xs': dt.tokens['space-base-xs'],
        'spacing-m': dt.tokens['space-base-m'],
        'distance-color': gt.graphDataText.textColor,
        fg: dt.reportTokens.stateStandard.normal.fg,
    }, 'geoRoute');
    const tableStyles = renderInlineCssVars({
        'table-bg': 'transparent',
        'table-row-bg': 'transparent',
        'table-scroll-bar-space': 0,
        'table-last-column-pd-adjustment': '-1rem',
        'table-cell-pd-inline-start-adjustment': '-1rem',
    });
    const menuSections = useMemo(() => {
        let sections = [
            {
                items: [
                    {
                        text: localize({ key: 'RoutePanel.menu.clear.txt' }),
                    },
                    {
                        text: localize({ key: 'RoutePanel.menu.reverse.txt' }),
                    },
                    {
                        text: localize({ key: 'RoutePanel.menu.add.waypoint.txt' }),
                        disabled: props.route.routeLocations.length >= 99,
                    },
                    {
                        text: localize({ key: 'RoutePanel.menu.delete.waypoint.txt' }),
                        disabled: !selectedRouteLocations ||
                            selectedRouteLocations.size === 0 ||
                            props.route.routeLocations.length - selectedRouteLocations.size < 2,
                    },
                ],
            },
        ];
        if (props.linkingEnabled) {
            sections = sections.concat([
                {
                    items: [
                        {
                            text: localize({ key: 'RoutePanel.menu.unlink.txt' }),
                            disabled: !selectedRouteLocations ||
                                Array.from(selectedRouteLocations)
                                    .map((index) => props.route.routeLocations[parseInt(index)])
                                    .filter((routeLocation) => typeof routeLocation === 'number').length === 0,
                        },
                    ],
                },
            ]);
        }
        // add distance unit selections and delete option
        sections = sections.concat([
            {
                items: [
                    {
                        text: localize({ key: 'TravelTypeField.distanceTypes.distance.unit.txt' }),
                        subSections: [
                            {
                                items: [
                                    {
                                        text: localize({ key: 'GeographicAreaPanel.distanceUnits.miles.txt' }),
                                        checked: props.route.unit === MapUnits.MILE,
                                    },
                                    {
                                        text: localize({ key: 'GeographicAreaPanel.distanceUnits.kilometers.txt' }),
                                        checked: props.route.unit === MapUnits.KILOMETER,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        text: localize({ key: 'RoutePanel.menu.delete.txt' }),
                    },
                ],
            },
        ]);
        return sections;
    }, [
        props.route.routeLocations,
        selectedRouteLocations,
        props.linkingEnabled,
        props.route.unit,
        localize,
    ]);
    // sync map route selections with the route list table
    useEffect(() => {
        setSelectRows({
            selectedRows: selectedRouteLocations,
        });
    }, [selectedRouteLocations, setSelectRows]);
    // sync table row selection with map route waypoints
    useEffect(() => {
        // do we have some table rows now exceeding number of waypoints, e.g. after removal?
        if ([...(selectedRows || [])].some((l) => parseInt(l) >= props.route.routeLocations.length)) {
            setSelectAllRows({ selectAll: false });
        }
        else {
            if (updateRouteSelection.current) {
                updateRouteSelection.current = false;
                props.actions.notifyUpdateRoute(props.id, {
                    index: props.featureIndex,
                    update: {
                        routeLocations: props.route.routeLocations.map((routeLocation, index) => {
                            if (typeof routeLocation === 'number') {
                                return routeLocation;
                            }
                            return Object.assign(Object.assign({}, routeLocation), { selected: selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.has(index.toString()) });
                        }),
                    },
                });
            }
        }
    }, [
        selectedRows,
        setSelectAllRows,
        props.actions,
        props.route.routeLocations,
        props.id,
        props.featureIndex,
    ]);
    // scroll to the route statistics if required
    useEffect(() => {
        var _a, _b;
        if (props.route.isRendered &&
            (((_a = previousRoute.current) === null || _a === void 0 ? void 0 : _a.distance) !== props.route.routeTravelDistance ||
                ((_b = previousRoute.current) === null || _b === void 0 ? void 0 : _b.duration) !== props.route.routeTravelDuration)) {
            const timoutId = setTimeout(() => {
                var _a;
                previousRoute.current = {
                    distance: props.route.routeTravelDistance,
                    duration: props.route.routeTravelDuration,
                };
                (_a = measureContainerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
            }, 50);
            return () => clearTimeout(timoutId);
        }
    }, [props.route.routeTravelDistance, props.route.routeTravelDuration, props.route.isRendered]);
    useOnUnmount(() => {
        // Disable pin mode on unmount
        props.actions.update(props.id, {
            pinModeEnabled: false,
            locationCandidate: undefined,
            searchHints: [],
        });
    });
    return (React.createElement("section", { "aria-labelledby": headingId, className: cn.formContainer, style: geoRouteStyles, tabIndex: -1 },
        React.createElement(PanelHeader, { id: props.id, ref: headerRef, headingId: headingId, type: 'route', text: localize({ key: 'RoutePanel.title.txt' }), onMenuItemSelected: (location) => {
                const section = location[0];
                if (section.length === 2) {
                    switch (section[0]) {
                        case 0:
                            switch (section[1]) {
                                case 0: // clear route
                                    clearRouteLocations();
                                    break;
                                case 1: // reverse route
                                    reverseRouteLocations();
                                    break;
                                case 2: // add route location
                                    addRouteLocation();
                                    break;
                                case 3: // delete route location
                                    deleteSelectedRouteLocations();
                                    break;
                            }
                            break;
                        case 1:
                            switch (section[1]) {
                                case 0:
                                    if (props.linkingEnabled) {
                                        // unlink
                                        unlinkRouteLocations();
                                    }
                                    else {
                                        // toggle distance unit
                                        props.actions.notifyUpdateRoute(props.id, {
                                            index: props.featureIndex,
                                            update: {
                                                unit: location[1][1] === 0 ? MapUnits.MILE : MapUnits.KILOMETER,
                                            },
                                        });
                                    }
                                    break;
                                case 1:
                                    if (props.linkingEnabled) {
                                        // toggle distance unit
                                        props.actions.notifyUpdateRoute(props.id, {
                                            index: props.featureIndex,
                                            update: {
                                                unit: location[1][1] === 0 ? MapUnits.MILE : MapUnits.KILOMETER,
                                            },
                                        });
                                    }
                                    else {
                                        // Delete route
                                        validateRouteLocations.current = false;
                                        props.actions.notifyDeletePins(props.id, [props.featureIndex]);
                                    }
                                    break;
                            }
                            break;
                        case 2:
                            switch (section[1]) {
                                case 0: // distance unit (options moves to 3rd menu position if linking is enabled)
                                    props.actions.notifyUpdateRoute(props.id, {
                                        index: props.featureIndex,
                                        update: {
                                            unit: location[1][1] === 0 ? MapUnits.MILE : MapUnits.KILOMETER,
                                        },
                                    });
                                    break;
                                case 1: // Delete route
                                    validateRouteLocations.current = false;
                                    props.actions.notifyDeletePins(props.id, [props.featureIndex]);
                                    break;
                            }
                            break;
                    }
                }
            }, menuSections: menuSections }),
        React.createElement("div", { className: cn.formScrollContainer },
            React.createElement(Flow, { spacing: "m", orientation: "vertical", className: cn.formContent },
                React.createElement(Flow, { className: cn.formField, orientation: "vertical", spacing: "s" },
                    React.createElement(Label, null, localize({ key: 'RoutePanel.label.txt' })),
                    React.createElement(Flow, { className: cx(cn.formField, cn.formFieldCentered), orientation: "horizontal", spacing: "s" },
                        React.createElement(Input, { value: nameInputValidator.value, onBlur: nameInputValidator.handleBlur, onCommit: nameInputValidator.handleCommit, message: (_d = nameInputValidator.errors) === null || _d === void 0 ? void 0 : _d.message, status: (_e = nameInputValidator.errors) === null || _e === void 0 ? void 0 : _e.status, onCancel: nameInputValidator.handleCancel, onChange: (event) => {
                                nameInputValidator.handleChange(event);
                                props.actions.notifyUpdateRoute(props.id, {
                                    index: props.featureIndex,
                                    update: { name: event.value },
                                });
                            }, className: cn.formField }),
                        React.createElement(FeatureColorPicker, { id: props.id, feature: props.route, featureIndex: props.featureIndex, onColorChange: (color) => {
                                props.actions.notifyUpdateRoute(props.id, {
                                    index: props.featureIndex,
                                    update: { color },
                                });
                            } }))),
                React.createElement(TravelTypeField, { canDoPremiumServices: props.canDoPremiumServices, feature: props.route, onTravelTypeChange: (mode) => {
                        props.actions.notifyUpdateRoute(props.id, {
                            index: props.featureIndex,
                            update: {
                                mode,
                            },
                        });
                    }, onTrafficTypeChange: (type) => {
                        var _a, _b;
                        const now = new Date();
                        props.actions.notifyUpdateRoute(props.id, {
                            index: props.featureIndex,
                            update: Object.assign(Object.assign({ traffic: type }, (type === DriveTimeTrafficType.HISTORICAL_AVERAGE && {
                                trafficTime: {
                                    day: now.getDay(),
                                    hour: (_a = now.getHours()) !== null && _a !== void 0 ? _a : 0,
                                    minute: (_b = now.getMinutes()) !== null && _b !== void 0 ? _b : 0,
                                },
                            })), (type === DriveTimeTrafficType.LIVE && {
                                trafficTime: {
                                    hour: 0,
                                },
                            })),
                        });
                    }, onTrafficTimeChange: (trafficTime) => {
                        props.actions.notifyUpdateRoute(props.id, {
                            index: props.featureIndex,
                            update: {
                                trafficTime,
                            },
                        });
                    }, onTrafficRefresh: (refreshAll) => {
                        if (refreshAll) {
                            props.actions.notifyDrawAll(props.id);
                        }
                        else {
                            props.actions.notifyDrawRoute(props.id, props.featureIndex);
                        }
                    } }),
                React.createElement("div", { ref: setListParentRef },
                    React.createElement(Flow, { spacing: "s", orientation: 'horizontal', className: cn.locationHeader },
                        React.createElement(Suspense, { fallback: null },
                            React.createElement(Label, { className: cn.formHeader, wrapText: false, helpPopup: {
                                    library: 'vaeua-gui-eua',
                                    contentKey: 'react.va.geo.search.routeLocations',
                                }, id: `${props.id}-locations` }, localize({ key: 'RoutePanel.locations.txt' }))),
                        React.createElement(Toolbar, { items: [
                                {
                                    type: 'Button',
                                    overflowPriority: 'never',
                                    variant: ButtonVariant.Light,
                                    icon: { icon: ReverseRoute },
                                    onPress: reverseRouteLocations,
                                },
                                {
                                    type: 'Button',
                                    overflowPriority: 'high',
                                    variant: ButtonVariant.Light,
                                    icon: { icon: Add },
                                    onPress: addRouteLocation,
                                    disabled: props.route.routeLocations.length >= 99 || !routeLocationsValid,
                                    tooltip: localize({ key: 'RoutePanel.location.add.txt' }),
                                },
                                {
                                    type: 'Button',
                                    id: 'BUTTON_UNLINK_LOCATION',
                                    overflowPriority: 'high',
                                    variant: ButtonVariant.Light,
                                    icon: { icon: LinkBroken },
                                    onPress: unlinkRouteLocations,
                                    disabled: !selectedRouteLocations ||
                                        Array.from(selectedRouteLocations)
                                            .map((index) => props.route.routeLocations[parseInt(index)])
                                            .filter((routeLocation) => typeof routeLocation === 'number').length ===
                                            0,
                                },
                                {
                                    type: 'Button',
                                    overflowPriority: 'high',
                                    variant: ButtonVariant.Light,
                                    icon: { icon: Delete },
                                    onPress: deleteSelectedRouteLocations,
                                    disabled: !selectedRouteLocations ||
                                        selectedRouteLocations.size === 0 ||
                                        props.route.routeLocations.length - selectedRouteLocations.size < 2,
                                    tooltip: localize({ key: 'RoutePanel.location.delete.txt' }),
                                },
                            ].filter((item) => !(item.id === 'BUTTON_UNLINK_LOCATION' && !props.linkingEnabled)) })),
                    React.createElement(Divider, { variant: "standard" }),
                    React.createElement(DragAndDropProvider, { enableDragDrop: true },
                        React.createElement(List, Object.assign({}, listState, { style: tableStyles, width: listParentDimensions === null || listParentDimensions === void 0 ? void 0 : listParentDimensions.width, className: cn.formTable, dndPrefix: "dndRouteLocation", expandToFillWidth: true, height: numberOfRows * 50, itemHeight: () => 50, dragEnabled: true, dropEnabled: true, rowCount: numberOfRows, outerBorderVisible: false, disableAutoFocus: true, ariaLabelledBy: `${props.id}-locations`, selectionMode: "desktop", singleSelectionOverrides: {
                                allowDeselection: true,
                            }, onSelect: (selectedRows) => {
                                setSelectRows({ selectedRows });
                                updateRouteSelection.current = true;
                            }, rowContainer: (index, children, className) => {
                                const filter = (selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.has('' + index))
                                    ? 'var(--table-selected-internal-filter)'
                                    : undefined;
                                return (React.createElement("div", { className: cx(className, cn.formRowContainer) },
                                    index !== 0 && (React.createElement(Icon, { icon: GrabberVertical, color: "neutral", className: cn.formDashIcon, style: { filter }, tooltip: "" })),
                                    children,
                                    index !== props.route.routeLocations.length - 1 && (React.createElement(Icon, { icon: GrabberVertical, color: "neutral", className: cn.formDashIcon, style: { filter }, tooltip: "" }))));
                            }, itemColumn: {
                                key: 'itemBody',
                                containsInteractiveContent: true,
                                item: (rowIndex, ref) => {
                                    var _a;
                                    const routeLocationOrLinkedIndex = props.route.routeLocations[rowIndex];
                                    const isLinkedPin = typeof routeLocationOrLinkedIndex === 'number';
                                    const waypoint = typeof routeLocationOrLinkedIndex === 'number'
                                        ? (_a = props.features) === null || _a === void 0 ? void 0 : _a[routeLocationOrLinkedIndex]
                                        : routeLocationOrLinkedIndex;
                                    if (!waypoint) {
                                        return null;
                                    }
                                    const searchFieldName = localize({
                                        key: 'LocationPopover.route.title.txt',
                                        args: {
                                            label: `${rowIndex + 1}`,
                                        },
                                    });
                                    return (React.createElement(Flow, { key: `${rowIndex}`, spacing: 's', orientation: 'horizontal', className: cn.formField, onMouseDown: (event) => {
                                            setSelectRow({
                                                index: rowIndex,
                                                selected: !(selectedRouteLocations === null || selectedRouteLocations === void 0 ? void 0 : selectedRouteLocations.has(rowIndex.toString())),
                                                selectionType: 'cell',
                                                clearPrevious: !event.ctrlKey,
                                                rowCount: numberOfRows,
                                                rangeSelection: event.shiftKey,
                                                rowKey: (index) => index.toString(),
                                            });
                                            updateRouteSelection.current = true;
                                        } },
                                        React.createElement("div", { className: cn.formRouteIcon },
                                            React.createElement(Icon, { icon: isLinkedPin ? GeoUIRoutePinLocation : BpmnEndEvent, className: cn.formIconLarge, style: preventColorInversionStyles(waypoint, props.route.color), tokens: () => getFeatureTokens(dt, props.route) }),
                                            React.createElement(Label, { className: cn.formRouteIconLabel, style: preventColorInversionStyles(waypoint, props.route.color), theme: () => {
                                                    return { '--label-fg': getFeatureTokens(dt, props.route).fg };
                                                } }, String(rowIndex + 1))),
                                        React.createElement("div", { className: cn.formField, style: { width: 'calc(100% - 3rem)' } },
                                            React.createElement(PanelSearchField, { id: props.id, ref: (searchFieldRef) => {
                                                    routeLocationRefs.createSetRef(rowIndex)(searchFieldRef);
                                                    if (ref) {
                                                        ref.current = searchFieldRef;
                                                    }
                                                }, feature: waypoint, includePinResults: true, hintIconOverride: ConditionalHighlightingBullet, disableAutoFocus: true, ariaLabel: searchFieldName, locationPopoverTitle: searchFieldName, locationCandidate: locationCandidateIndex === rowIndex
                                                    ? props.locationCandidate
                                                    : undefined, onInvalidateLocation: () => {
                                                    props.actions.notifyUpdateRoute(props.id, {
                                                        index: props.featureIndex,
                                                        update: {
                                                            routeLocations: props.route.routeLocations.map((routeLocation, index) => {
                                                                if (index === rowIndex && typeof routeLocation !== 'number') {
                                                                    routeLocation = Object.assign(Object.assign({}, routeLocation), { latLon: [NaN, NaN], location: undefined });
                                                                }
                                                                return routeLocation;
                                                            }),
                                                        },
                                                    });
                                                }, onUpdateHints: ({ searchTerm }) => {
                                                    props.actions.notifyUpdateHints(props.id, {
                                                        featureIndex: props.featureIndex,
                                                        searchTerm,
                                                    });
                                                }, onApplyHint: ({ hint, searchTerm }) => {
                                                    props.actions.notifyApplyHint(props.id, {
                                                        featureIndex: props.featureIndex,
                                                        waypointIndex: rowIndex,
                                                        hint,
                                                        searchTerm,
                                                    });
                                                }, onApplyPin: ({ pin }) => {
                                                    props.actions.notifyUpdateRoute(props.id, {
                                                        index: props.featureIndex,
                                                        update: {
                                                            routeLocations: props.route.routeLocations.map((routeLocation, index) => {
                                                                if (index === rowIndex && typeof routeLocation !== 'number') {
                                                                    routeLocation = Object.assign(Object.assign({}, routeLocation), { latLon: pin.latLon, location: pin.location });
                                                                }
                                                                return routeLocation;
                                                            }),
                                                        },
                                                    });
                                                }, onUpdatePinMode: (enabled) => {
                                                    if (enabled || locationCandidateIndex === rowIndex) {
                                                        setLocationCandidateIndex(rowIndex);
                                                        props.actions.setPinModeEnabled(props.id, enabled);
                                                    }
                                                }, onConfirm: () => {
                                                    props.actions.update(props.id, {
                                                        pinModeEnabled: false,
                                                        locationCandidate: undefined,
                                                    });
                                                    props.actions.notifyUpdateRoute(props.id, {
                                                        index: props.featureIndex,
                                                        update: {
                                                            routeLocations: props.route.routeLocations.map((routeLocation, index) => {
                                                                var _a, _b;
                                                                if (index === rowIndex && typeof routeLocation !== 'number') {
                                                                    routeLocation = Object.assign(Object.assign(Object.assign({}, routeLocation), { latLon: props.locationCandidate.latLon, location: (_a = props.locationCandidate.location) !== null && _a !== void 0 ? _a : undefined }), (((_b = props.locationCandidate) === null || _b === void 0 ? void 0 : _b.country) && {
                                                                        country: { id3: props.locationCandidate.country },
                                                                    }));
                                                                }
                                                                return routeLocation;
                                                            }),
                                                        },
                                                    });
                                                }, onCancel: () => {
                                                    props.actions.update(props.id, {
                                                        pinModeEnabled: false,
                                                        locationCandidate: undefined,
                                                    });
                                                }, onOutsideClick: () => {
                                                    props.actions.update(props.id, {
                                                        locationCandidate: undefined,
                                                    });
                                                } })),
                                        React.createElement(Icon, { className: cn.formGrabberIcon, icon: GrabberReorderList })));
                                },
                            }, accessibilityInfo: {
                                getCellInfo: ({ rowIndex, columnIndex }) => {
                                    var _a;
                                    const routeLocationOrLinkedIndex = props.route.routeLocations[rowIndex];
                                    const value = (typeof routeLocationOrLinkedIndex === 'number'
                                        ? (_a = props.features) === null || _a === void 0 ? void 0 : _a[routeLocationOrLinkedIndex]
                                        : routeLocationOrLinkedIndex);
                                    const placeholder = localize({ key: 'PinPanel.searchPlaceholder.txt' });
                                    return columnIndex === 0
                                        ? localize({
                                            key: 'RoutePanel.accessibilityCellInfo.fmt',
                                            args: {
                                                index: `${rowIndex + 1}`,
                                                location: value.location ? value.location : placeholder,
                                            },
                                        })
                                        : null;
                                },
                            }, reorderItem: reorderTableRows, getRowKey: (rowIndex) => rowIndex.toString() })))),
                React.createElement(Flow, { ref: measureContainerRef, spacing: 'none', orientation: 'vertical', style: { flexDirection: isTimeTravel ? 'column-reverse' : 'column' } },
                    props.route.routeTravelDistance && props.route.isRendered ? (React.createElement(Flow, { spacing: 's', orientation: 'horizontal' },
                        React.createElement(Label, null, localize({ key: 'RoutePanel.distance.txt' })),
                        React.createElement(Label, { className: cn.formRouteDistance }, props.route.routeTravelDistance))) : null,
                    props.route.routeTravelDuration && props.route.isRendered && isTimeTravel ? (React.createElement(Flow, { spacing: 's', orientation: 'horizontal' },
                        React.createElement(Label, null, localize({ key: 'RoutePanel.duration.txt' })),
                        React.createElement(Label, { className: cn.formRouteDistance }, props.route.routeTravelDuration))) : null),
                props.route.routeWidth ? (React.createElement(Checkbox, { label: localize({ key: 'RoutePanel.route.width.txt' }), checked: routeWidthEnabled, onPress: () => {
                        props.actions.notifyUpdateRoute(props.id, {
                            index: props.featureIndex,
                            update: {
                                routeWidth: {
                                    mode: RoutingMode.DIRECT,
                                },
                            },
                        });
                        setRouteWidthEnabled(!routeWidthEnabled);
                    } })) : null,
                routeWidthEnabled && props.route.routeWidth && (React.createElement(Form, { dependency: true, labelPosition: "top", className: cn.formField },
                    React.createElement(TravelTypeField, { canDoPremiumServices: props.canDoPremiumServices, feature: props.route.routeWidth, onTravelTypeChange: (mode) => {
                            props.actions.notifyUpdateRoute(props.id, {
                                index: props.featureIndex,
                                update: {
                                    routeWidth: Object.assign(Object.assign({}, props.route.routeWidth), { mode }),
                                },
                            });
                        }, onTrafficTypeChange: (type) => {
                            var _a, _b;
                            const now = new Date();
                            props.actions.notifyUpdateRoute(props.id, {
                                index: props.featureIndex,
                                update: {
                                    routeWidth: Object.assign(Object.assign(Object.assign(Object.assign({}, props.route.routeWidth), { traffic: type }), (type === DriveTimeTrafficType.HISTORICAL_AVERAGE && {
                                        trafficTime: {
                                            day: now.getDay(),
                                            hour: (_a = now.getHours()) !== null && _a !== void 0 ? _a : 0,
                                            minute: (_b = now.getMinutes()) !== null && _b !== void 0 ? _b : 0,
                                        },
                                    })), (type === DriveTimeTrafficType.LIVE && {
                                        trafficTime: {
                                            hour: 0,
                                        },
                                    })),
                                },
                            });
                        }, onTrafficTimeChange: (trafficTime) => {
                            props.actions.notifyUpdateRoute(props.id, {
                                index: props.featureIndex,
                                update: {
                                    routeWidth: Object.assign(Object.assign({}, props.route.routeWidth), { trafficTime }),
                                },
                            });
                        }, onTrafficRefresh: (refreshAll) => {
                            if (refreshAll) {
                                props.actions.notifyDrawAll(props.id);
                            }
                            else {
                                props.actions.notifyDrawRoute(props.id, props.featureIndex);
                            }
                        } }),
                    React.createElement(NumericStepperWithLabels, { label: localize({
                            key: isRouteWidthTimeTravel
                                ? 'GeographicAreaPanel.time.minutes.txt'
                                : props.route.unit === MapUnits.MILE
                                    ? 'GeographicAreaPanel.distance.miles.txt'
                                    : 'GeographicAreaPanel.distance.kilometer.txt',
                        }), className: cn.formField, minimum: 0, maximum: 99, stepSize: 1, onChange: (event) => {
                            var _a;
                            props.actions.notifyUpdateRoute(props.id, {
                                index: props.featureIndex,
                                update: {
                                    routeWidth: Object.assign(Object.assign({}, props.route.routeWidth), { amount: [(_a = event.numericValue) !== null && _a !== void 0 ? _a : 0] }),
                                },
                            });
                        }, editMode: "combo", value: ((_g = (_f = props.route.routeWidth) === null || _f === void 0 ? void 0 : _f.amount) === null || _g === void 0 ? void 0 : _g.length) === 1
                            ? (_h = props.route.routeWidth) === null || _h === void 0 ? void 0 : _h.amount[0]
                            : 1 }))))),
        React.createElement(Divider, { variant: 'standard' }),
        React.createElement("div", { ref: footerRef, className: cn.footer },
            React.createElement(Button, { className: cn.footerButton, disabled: !isFormValid, text: localize({
                    key: props.route.isRendered === undefined
                        ? 'RoutePanel.draw.txt'
                        : 'RoutePanel.update.txt',
                }), onPress: () => {
                    props.actions.notifyDrawRoute(props.id, props.featureIndex);
                } }))));
};
export const RoutePanel = connect((state, props) => {
    var _a;
    const { features, autoLinkToPins, routeArrows, routeArrowType, linkingEnabled, canDoPremiumServices, pinModeEnabled, locationCandidate, } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    const route = features === null || features === void 0 ? void 0 : features[props.featureIndex];
    if (!route || route.type !== 'route') {
        throw new Error('Route not found');
    }
    return {
        autoLinkToPins,
        route,
        routeArrows,
        routeArrowType,
        features,
        linkingEnabled,
        canDoPremiumServices,
        pinModeEnabled,
        locationCandidate,
    };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(RoutePanelBase);
